import * as yup from "yup";
import {capitalize} from "@mui/material";
import {FormikConfig, FormikHelpers} from "formik";
import {useCreateAufgabe} from "../../client/hooks";
import {useCallback, useMemo} from "react";
import {Aufgabe, AufgabeDaten, AufgabeStatus, PromptTemplate} from "../../api";
import {useAufgabenHandling} from "./useAufgabenHandling";
import {useSnackbar} from "notistack";
import {useNavigate} from "react-router-dom";
import {useMutation, useQueryClient} from "react-query";
import {useMandantenHandling} from "../mandanten/useMandantenHandling";
import {DEFAULT_LANGUAGE} from "../../config/language";
import {useAufgabePromptTemplate} from "../../client/queries";
import {useOnboardingHandling} from "../onboarding/useOnboardingHandling";
import { DEFAULT_EMPATHY } from "../../config";
import type { AufgabeFormValues } from "../../types/aufgabenTypes";
import { buildInitialAufgabeFormValues } from "./utils/buildInitialAufgabeFormValues";

export function useAufgabeCreateForm(): FormikConfig<AufgabeFormValues> {
    const {translation} = useAufgabenHandling();
    const {getCountryCode} = useOnboardingHandling();
    const {getMandantIdByObject} = useMandantenHandling();
    const {enqueueSnackbar} = useSnackbar();
    const navigate = useNavigate();
    const {mutateAsync} = useAufgabeCreate();
    const defaultPromptTemplate: PromptTemplate = useAufgabePromptTemplate(DEFAULT_LANGUAGE.split("-")[0]);

    const schema = yup.object().shape({
        titel: yup.string().required(capitalize(translation('task-attributes-title-required-message'))),
        beschreibung: yup.string().nullable(),
        sprache: yup.string().matches(/^[a-z]{2}-[A-Z]{2}$/, capitalize(translation('task-attributes-language-regex-message'))),
        prompt: yup.string().required(capitalize(translation('task-attributes-prompt-required-message'))),
        foreignPrompt: yup.string().when('sprache', {
                is: (lang: typeof DEFAULT_LANGUAGE) => lang !== DEFAULT_LANGUAGE,
                then: s => s.trim().min(1, translation('task-attributes-prompt-required-message')).required(translation('task-attributes-prompt-required-message')),
                otherwise: s => s.notRequired(),
            }),
        stimmeId: yup.string().transform((v) => (v === '' ? null : v)).nullable(),
        telefonnummer: yup.string()
            .transform((v) => (v === '' ? null : v))
            .nullable()
            .matches(/^[1-9][0-9]*$/, capitalize(translation('task-test-attributes-phone-validation-failed-message'))),
        mandantId: yup.string().required(capitalize(translation('task-attributes-client-required-message'))),
        schedulerId: yup.string().required(capitalize(translation('task-attributes-scheduler-required-message'))),
        status: yup.string().oneOf(['PAUSIERT', 'LIVE', 'ABGESCHLOSSEN']).required(capitalize(translation('task-attributes-status-required-message'))),
        auftragssystemAuftragId: yup.string().required(),
        speechModel: yup.string().transform((v) => (v === '' ? null : v)).nullable()
    });

    const initialTouched = useMemo(() => ({
        titel: false,
        beschreibung: false,
        sprache: false,
        prompt: false,
        foreignPrompt: false,
        stimmeId: false,
        empathie: false,
        telefonnummer: false,
        mandantId: false,
        schedulerId: false,
        status: false,
        inboundConfigDurationInHours: false,
        useInbound: false,
        updateInboundConfigTimeOnUpdate: false,
        useInboundPrompt: false,
        useSeparateToolsForInbound: false,
        anzahlAnrufversucheMax: false,
        auftragssystemAuftragId: false,
        speechModel: false,
    }), []);

    const initialValues = useMemo(
        () => buildInitialAufgabeFormValues({
            defaultPrompt: defaultPromptTemplate?.template,
        }),
        [defaultPromptTemplate?.template]
    );

    const onSubmitCreateForm = useCallback(async (
        daten: AufgabeFormValues,
        {setSubmitting, setStatus}: FormikHelpers<AufgabeFormValues>
    ): Promise<void> => {
        try {
            const aufgabe: Aufgabe = await mutateAsync({
                titel: daten.titel,
                beschreibung: daten.beschreibung,
                sprache: daten.sprache,
                initialPrompt: {
                    versionName: "Initial",
                    prompt: daten.prompt,
                    foreignLanguagePrompt: daten.foreignPrompt || undefined,
                },
                stimmeId: daten.stimmeId || undefined,
                telefonnummer: daten.telefonnummer?.trim() ? `${getCountryCode()}${daten.telefonnummer.trim()}` : null,
                empathie: daten.empathie,
                mandantId: getMandantIdByObject(daten.mandantId),
                schedulerId: daten.schedulerId,
                status: daten.status as AufgabeDaten['status'],
                auftragssystem_auftrag_id: daten.auftragssystemAuftragId,
                inbound_config_duration_in_hours: daten.inboundConfigDurationInHours,
                use_inbound: daten.useInbound,
                update_inbound_config_time_on_update: daten.updateInboundConfigTimeOnUpdate,
                use_inbound_prompt: daten.useInboundPrompt,
                use_separate_tools_for_inbound: daten.useSeparateToolsForInbound,
                anzahl_anrufversuche_max: daten.anzahlAnrufversucheMax,
                speechModel: daten.speechModel || undefined
            });
            setStatus(undefined);
            enqueueSnackbar(capitalize(translation('task-creation-successful')), {
                variant: "success",
            });
            navigate(`/aufgaben/${aufgabe.id}`);
        } catch (error: unknown) {
            enqueueSnackbar(capitalize(translation('task-creation-failed')), {
                variant: "error",
            });
            if (error instanceof Error) {
                setStatus(error.message);
            } else {
                setStatus("An unknown error occurred.");
            }
        } finally {
            setSubmitting(false);
        }
    }, [mutateAsync, enqueueSnackbar, translation, navigate, getMandantIdByObject, getCountryCode]);

    return useMemo(
        () => ({
            validationSchema: schema,
            validateOnMount: true,
            enableReinitialize: true,
            initialTouched: initialTouched,
            initialValues: initialValues,
            onSubmit: onSubmitCreateForm
        }),
        [schema, initialValues, initialTouched, onSubmitCreateForm]
    );
}

function useAufgabeCreate() {
    const createAufgabe = useCreateAufgabe();
    const queryClient = useQueryClient();

    return useMutation(
        (input: AufgabeDaten): Promise<Aufgabe> => {
            return createAufgabe(input);
        },
        {
            onSuccess: async () => {
                await queryClient.invalidateQueries(["aufgaben"]);
            },
        }
    );
}
